// /admin/webhooks — outbound webhook CRUD + test-fire + delivery log (T124).
//
// Layout:
//   - Page header with "Add webhook" button.
//   - Endpoint list: URL, project_id badge, event tags, enabled toggle,
//     test-fire button, delete button.
//   - Expandable delivery log panel per endpoint (last 50 deliveries):
//     attempted_at, event, http_status chip, latency, error.
//   - "Add webhook" → inline form above the list.

import { FormEvent, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import {
  Activity,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Plus,
  Trash2,
  Webhook,
  X,
} from 'lucide-react';
import {
  ADMIN_WEBHOOKS_KEY,
  WebhookDelivery,
  WebhookEndpoint,
  adminWebhooksApi,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

// agent.telemetry (T1015) is org-wide, not project-scoped like the other
// three — any endpoint here, regardless of which project it's registered
// against, receives it once subscribed. See webhook.DispatchOrgWide.
const WEBHOOK_EVENTS = ['scan.completed', 'gate.failed', 'agent.telemetry'] as const;

export function AdminWebhooks() {
  const { data, error, isLoading, mutate } = useSWR<WebhookEndpoint[]>(
    ADMIN_WEBHOOKS_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [showCreate, setShowCreate] = useState(false);

  const handleCreated = () => {
    setShowCreate(false);
    mutate();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this webhook endpoint? Deliveries will be lost.')) return;
    await adminWebhooksApi.delete(id);
    mutate();
  };

  const handleToggle = async (ep: WebhookEndpoint) => {
    await adminWebhooksApi.update(ep.id, { enabled: !ep.enabled });
    mutate();
  };

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Webhook />}
        title="Webhooks"
        description={
          <>
            Outbound HMAC-signed webhooks notify external systems when scans
            complete or quality gates fail. Each endpoint receives a signed
            POST with the event payload.
          </>
        }
        actions={
          showCreate ? null : (
            <button onClick={() => setShowCreate(true)} className={primaryBtnCls}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add webhook
            </button>
          )
        }
      />

      {showCreate && (
        <CreateWebhookForm
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {(() => {
        if (isLoading) return (
          <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="text-sm">Loading webhooks…</span>
          </div>
        );
        if (error) return <ErrorBanner />;
        if (!data || data.length === 0) return (
          <div className="orb-card p-8 text-center text-fg-muted text-sm">
            No webhook endpoints yet. Add one to start receiving event notifications.
          </div>
        );
        return (
          <EndpointList
            endpoints={data}
            onDelete={handleDelete}
            onToggle={handleToggle}
          />
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

interface CreateFormProps {
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}

function CreateWebhookForm({ onCreated, onCancel }: CreateFormProps) {
  const [projectID, setProjectID] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<Set<string>>(
    new Set(['scan.completed', 'gate.failed']),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleEvent = (e: string) => {
    const next = new Set(events);
    if (next.has(e)) next.delete(e);
    else next.add(e);
    setEvents(next);
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    if (!projectID.trim()) { setErr('Project ID is required.'); return; }
    if (!url.trim()) { setErr('URL is required.'); return; }
    if (!url.startsWith('https://')) { setErr('URL must start with https://.'); return; }
    if (!secret.trim()) { setErr('Secret is required.'); return; }
    if (events.size === 0) { setErr('Select at least one event.'); return; }
    setSaving(true);
    try {
      await adminWebhooksApi.create({
        project_id: projectID.trim(),
        url: url.trim(),
        secret: secret.trim(),
        events: Array.from(events),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create webhook.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="orb-card mb-6 p-5 space-y-4">
      <p className="text-sm font-semibold text-fg">New webhook endpoint</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="wh-project" className="orb-section-title text-[11px]">
            Project ID
          </label>
          <input
            id="wh-project"
            type="text"
            value={projectID}
            onChange={(e) => setProjectID(e.target.value)}
            className={inputCls}
            placeholder="UUID of the project"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="wh-url" className="orb-section-title text-[11px]">
            Payload URL
          </label>
          <input
            id="wh-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className={inputCls}
            placeholder="https://example.com/hook"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="wh-secret" className="orb-section-title text-[11px]">
          Secret
        </label>
        <input
          id="wh-secret"
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          className={inputCls}
          placeholder="HMAC-SHA256 signing secret"
        />
        <p className="text-[11px] text-fg-muted">
          The server signs every POST with{' '}
          <code className="font-mono">X-Orbital-Signature-256</code>. Store
          this secret in your receiver and verify it.
        </p>
      </div>

      <div className="space-y-2">
        <p className="orb-section-title text-[11px]">Events</p>
        <div className="flex gap-4">
          {WEBHOOK_EVENTS.map((ev) => (
            <label key={ev} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={events.has(ev)}
                onChange={() => toggleEvent(ev)}
                className="accent-accent h-4 w-4"
              />
              <span className="font-mono text-xs text-fg">{ev}</span>
            </label>
          ))}
        </div>
      </div>

      {err && <p role="alert" className="text-xs text-danger">{err}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…</>
          ) : (
            'Add webhook'
          )}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-fg-muted hover:text-fg transition-colors px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Endpoint list
// ---------------------------------------------------------------------------

interface EndpointListProps {
  readonly endpoints: WebhookEndpoint[];
  readonly onDelete: (id: string) => void | Promise<void>;
  readonly onToggle: (ep: WebhookEndpoint) => void | Promise<void>;
}

function EndpointList({ endpoints, onDelete, onToggle }: EndpointListProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [firing, setFiring] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [fireResults, setFireResults] = useState<Record<string, { status: number; latency: number; err?: string }>>({});

  const handleToggle = async (ep: WebhookEndpoint) => {
    setToggling(ep.id);
    try { await onToggle(ep); } finally { setToggling(null); }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try { await onDelete(id); } finally { setDeleting(null); }
  };

  const handleTestFire = async (id: string) => {
    setFiring(id);
    try {
      const res = await adminWebhooksApi.testFire(id);
      setFireResults((prev) => ({ ...prev, [id]: { status: res.http_status, latency: res.latency_ms, err: res.error } }));
      // Refresh deliveries if expanded.
      if (expanded === id) {
        globalMutate(`${ADMIN_WEBHOOKS_KEY}/${id}/deliveries`);
      }
    } catch (e) {
      setFireResults((prev) => ({ ...prev, [id]: { status: 0, latency: 0, err: e instanceof Error ? e.message : 'failed' } }));
    } finally {
      setFiring(null);
    }
  };

  return (
    <div className="space-y-3">
      {endpoints.map((ep) => (
        <div key={ep.id} className="orb-card overflow-hidden">
          {/* Endpoint row */}
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => setExpanded(expanded === ep.id ? null : ep.id)}
              className="p-1 text-fg-muted hover:text-fg transition-colors"
              title={expanded === ep.id ? 'Collapse delivery log' : 'Expand delivery log'}
            >
              {expanded === ep.id
                ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                : <ChevronRight className="h-4 w-4" aria-hidden="true" />
              }
            </button>

            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm text-fg truncate">{ep.url}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="inline-block text-[10px] bg-bg-inset border border-border rounded px-1.5 py-0.5 text-fg-muted font-mono">
                  {ep.project_id.slice(0, 8)}…
                </span>
                {ep.events.map((e) => (
                  <span key={e} className="inline-block text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded font-mono">
                    {e}
                  </span>
                ))}
              </div>
            </div>

            {/* Test-fire result badge */}
            {fireResults[ep.id] && (
              <TestFireBadge result={fireResults[ep.id]} />
            )}

            {/* Test-fire button */}
            <button
              onClick={() => handleTestFire(ep.id)}
              disabled={firing === ep.id}
              className="p-1.5 rounded text-fg-muted hover:text-accent hover:bg-accent/10 transition-colors disabled:opacity-30"
              title="Test fire (sends a synthetic scan.completed event)"
            >
              {firing === ep.id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <Play className="h-3.5 w-3.5" aria-hidden="true" />
              }
            </button>

            {/* Enable/disable toggle */}
            <button
              onClick={() => handleToggle(ep)}
              disabled={toggling === ep.id}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-30 ${ep.enabled ? 'bg-accent' : 'bg-fg-subtle/30'}`}
              title={ep.enabled ? 'Disable endpoint' : 'Enable endpoint'}
              aria-label={ep.enabled ? 'Disable' : 'Enable'}
            >
              {toggling === ep.id
                ? <Loader2 className="h-3 w-3 text-white mx-auto animate-spin" aria-hidden="true" />
                : <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${ep.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              }
            </button>

            {/* Delete */}
            <button
              onClick={() => handleDelete(ep.id)}
              disabled={deleting === ep.id}
              className="p-1.5 rounded text-fg-muted hover:text-danger hover:bg-danger/10 transition-colors disabled:opacity-30"
              title="Delete endpoint"
            >
              {deleting === ep.id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              }
            </button>
          </div>

          {/* Delivery log panel */}
          {expanded === ep.id && (
            <DeliveryLog endpointID={ep.id} />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test-fire result badge
// ---------------------------------------------------------------------------

function TestFireBadge({ result }: { readonly result: { status: number; latency: number; err?: string } }) {
  const ok = result.status >= 200 && result.status < 300;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${ok ? 'bg-success/10 text-success border-success/30' : 'bg-danger/10 text-danger border-danger/30'}`}
      title={result.err}
    >
      {ok ? <Check className="h-2.5 w-2.5" aria-hidden="true" /> : <X className="h-2.5 w-2.5" aria-hidden="true" />}
      {result.status > 0 ? result.status : 'ERR'} · {result.latency}ms
    </span>
  );
}

// ---------------------------------------------------------------------------
// Delivery log
// ---------------------------------------------------------------------------

function DeliveryLog({ endpointID }: { readonly endpointID: string }) {
  const key = `${ADMIN_WEBHOOKS_KEY}/${endpointID}/deliveries`;
  const { data, error, isLoading } = useSWR<WebhookDelivery[]>(key, fetcher, {
    revalidateOnFocus: false,
  });

  if (isLoading) {
    return (
      <div className="border-t border-border px-4 py-3 flex items-center gap-2 text-fg-muted text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading deliveries…
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-border px-4 py-3 text-xs text-danger">
        Could not load delivery log.
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="border-t border-border px-4 py-3 text-xs text-fg-muted flex items-center gap-2">
        <Activity className="h-3.5 w-3.5" aria-hidden="true" />
        No deliveries recorded yet. Use the test-fire button to send a test event.
      </div>
    );
  }

  return (
    <div className="border-t border-border">
      <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
        Recent deliveries
      </p>
      <Table bare dense>
        <THead>
          <TR>
            <TH>Time</TH>
            <TH>Event</TH>
            <TH>Status</TH>
            <TH>Latency</TH>
            <TH>Error</TH>
          </TR>
        </THead>
        <TBody>
          {data.map((d) => {
            const ok = d.http_status >= 200 && d.http_status < 300;
            return (
              <TR key={d.id}>
                <TD mono>{fmtDate(d.attempted_at)}</TD>
                <TD mono className="text-accent">{d.event}</TD>
                <TD>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${ok ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                    {d.http_status > 0 ? d.http_status : 'ERR'}
                  </span>
                </TD>
                <TD mono>{d.latency_ms}ms</TD>
                <TD className="text-danger truncate max-w-[200px]" title={d.error}>
                  {d.error || '—'}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorBanner() {
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">Could not load webhook endpoints.</p>
      <p className="text-xs text-fg-muted mt-2">
        Only org admins can manage webhooks. Verify your role or check the API
        server logs.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers + styles
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
